import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getCompanyData,
  isToros,
  isWina,
  isLandToken,
  isMabra,
} from "../../utils";
import Container from "../Container/Container";
import PreconfirmationStep from "./PreconfirmationStep/PreconfirmationStep";
import { LoadingCircle } from "@almafintech/react-components/LoadingCircle";
import useWindowWidth from "../../hooks/useWindowWidth";
import {
  Preconfirmation,
  PreconfirmationStep as PreconfirmationStepType,
} from "../../types";

import torosCapitalLogo from "../../assets/images/logos/toros-capital.png";
import winaLogo from "../../assets/images/logos/wina.svg";
import landTokenLogo from "../../assets/images/logos/landtoken.svg";
import mabraLogo from "../../assets/images/logos/Mabra.svg";

import styles from "./PreconfirmationCard.module.scss";
import { RootState } from "../../app/store";
import { Tabs } from "@almafintech/react-components";

const PreconfirmationCard = ({
  preconfirmationSteps,
}: {
  preconfirmationSteps: PreconfirmationStepType[];
}) => {
  const {
    container,
    content,
    tabbedContainer,
    header,
    stepsContainer,
    loading,
    torosLogo,
    winaLogoStyles,
    companyLogo,
    logosContainer,
    landToken,
    mabra,
  } = styles;

  const { logo, logoClassname, companyName } = getCompanyData();

  const state = useAppSelector((state: RootState) => state);
  const { onboardingData, securities, activeTemplate } = state;

  const { preconfirmation, onboardingType } = activeTemplate;

  const { formValues } =
    onboardingType === "SECURITIES" ? securities : onboardingData;

  const { headerContent, tabs, onChangeTab, disabledKeys, defaultSelectedKey } =
    preconfirmation
      ? preconfirmation({ values: formValues })
      : ({} as Preconfirmation);

  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (onboardingType && formValues) setIsLoading(false);
  }, [onboardingType, formValues]);

  const dimensions = useWindowWidth();
  const isMobile = dimensions.width < 768;

  const logos = [
    {
      show: isLandToken(formValues),
      className: landToken,
      src: landTokenLogo,
      alt: "Land Token logo",
    },
    {
      show: isMabra(formValues),
      className: mabra,
      src: mabraLogo,
      alt: "Mabra logo",
    },
    {
      show: isToros(formValues),
      className: torosLogo,
      src: torosCapitalLogo,
      alt: "Toros Capital logo",
    },
    {
      show: isWina(formValues),
      className: winaLogoStyles,
      src: winaLogo,
      alt: "Wina logo",
    },
    {
      show: true,
      className: `${companyLogo} ${logoClassname ? styles[logoClassname] : ""}`,
      src: logo,
      alt: `${companyName} logo`,
    },
  ];

  const Header = () => (
    <div className={header}>
      {headerContent ? (
        headerContent
      ) : (
        <div>
          <h1>Preconfirmación de apertura</h1>
          <p>Revisá que los datos sean correctos.</p>
        </div>
      )}
      {!isMobile && (
        <div className={logosContainer}>
          {logos
            .filter(({ show }) => show)
            .map(({ alt, className, src }, i) => (
              <img key={alt + i} className={className} src={src} alt={alt} />
            ))}
        </div>
      )}
    </div>
  );

  const Steps = ({ steps }: { steps: PreconfirmationStep[] }) =>
    isLoading ? (
      <div className={loading}>
        <LoadingCircle width="50" />
      </div>
    ) : (
      <div className={stepsContainer}>
        {steps
          ?.filter((step) => !step.hideStep)
          .map((step) => (
            <PreconfirmationStep key={step.name} {...step} />
          ))}
      </div>
    );

  return tabs?.length ? (
    <div className={tabbedContainer}>
      <Tabs
        variant="underlined"
        handleChange={(key) =>
          onChangeTab ? onChangeTab({ key, state, dispatch }) : null
        }
        defaultSelectedKey={
          defaultSelectedKey
            ? defaultSelectedKey({ state, dispatch })
            : undefined
        }
        disabledKeys={disabledKeys ? disabledKeys({ state, dispatch }) : []}
        items={tabs.map(({ label, steps: tabSteps }, i) => ({
          id: i.toString(),
          label,
          content: (
            <Container key={i} className={content}>
              <Header />
              <Steps steps={tabSteps} />
            </Container>
          ),
        }))}
      />
    </div>
  ) : (
    <Container className={`${container} ${content}`}>
      <Header />
      <Steps steps={preconfirmationSteps} />
    </Container>
  );
};

export default PreconfirmationCard;
